/* Quote Request Form Handler — GTD
 * Klaviyo JS SDK identify + Client Subscriptions API + Client Events API + GTM dataLayer
 *
 * Three mechanisms fire on every submission:
 *   0. _learnq identify     — creates/updates profile with phone immediately (new AND existing profiles)
 *   1. Client Subscriptions API — adds profile to list; saves phone for new subscribers
 *   2. Client Events API        — fires the named metric to trigger the flow (always fires)
 *
 * Works for new AND existing Klaviyo subscribers — identified by email,
 * no cookie dependency.
 *
 * Handles every .inquiry-form on the page automatically — no per-product
 * wiring needed, since snippets/quote-request-popup.liquid gives each
 * form/container/thank-you triplet matching IDs:
 *   form:       quote-form-{product.id}
 *   container:  quote-form-container-{product.id}
 *   thank-you:  quote-thank-you-{product.id}
 *
 * Upload to: Shopify Admin → Themes → Edit code → Assets
 * Add to page template (main-product.liquid, once per page):
 *   <script src="{{ 'quote-request-klaviyo-submission.js' | asset_url }}" defer></script>
 */
(function () {

  /* ─── ACCOUNT CONFIGURATION ───────────────────────────────────────────────
   * These are the only values that change between Klaviyo / GTM accounts.
   * ───────────────────────────────────────────────────────────────────────── */
  var CONFIG = {
    // Klaviyo public Company ID
    // Where to find: Klaviyo Admin → Settings → Account → API Keys → Public API Key (6-character code)
    klaviyoCompanyId: 'VcCjVx',

    // Klaviyo list ID for quote-request leads
    // Where to find: Klaviyo Admin → Lists → your list → ID shown in the URL
    klaviyoListId: 'S7ck9x',

    // The metric name that triggers your Klaviyo flow.
    // Must match EXACTLY — Klaviyo is case-sensitive.
    // Where to find: Klaviyo Admin → Flows → your flow → trigger metric name
    klaviyoEventName: 'Quote Request Submitted',

    // GTM dataLayer event name.
    // Must match the "Event Name" field in your GTM Custom Event trigger.
    // Where to find: GTM → Triggers → your trigger → Event Name
    gtmEventName: 'Product Enquiry Form Submit',
    // gtmEventName: 'quote_request_submit',
  };

  /* ───────────────────────────────────────────────────────────────────────── */

  var LOG = '[quote-request-form]';
  var FORM_ID_PREFIX = 'quote-form-';


  function isTrackingTest() { return window.GTDChatraCollectorConfig?.themeRole === 'unpublished' || window.GTDChatraCollectorConfig?.testMode === true; }
  async function sendKlaviyo(url, options) {
    const isEvent = JSON.parse(options.body).data.type === 'event';
    for (let attempt = 0; attempt < (isEvent ? 3 : 1); attempt++) {
      let response;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
      try {
        response = await fetch(url, {...options, signal: controller.signal});
        if (response.ok) return response;
        if (response.status < 500 && ![408,425,429].includes(response.status)) throw new Error('provider_rejected');
      } catch (error) {
        if (error.message === 'provider_rejected' || !isEvent || attempt === 2) throw new Error('delivery_not_confirmed');
      } finally { clearTimeout(timer); }
      if (!isEvent || attempt === 2) throw new Error('delivery_not_confirmed');
      const retry = response?.headers?.get('Retry-After');
      const serverDelay = retry && /^\d+$/.test(retry) ? Number(retry)*1000 : Math.max(0, Date.parse(retry || '')-Date.now()) || 0;
      if (serverDelay > 15000) throw new Error('delivery_retry_later');
      await new Promise(resolve => setTimeout(resolve, Math.max(1000*2**attempt, serverDelay)));
    }
    throw new Error('delivery_not_confirmed');
  }
  function normalizePhone(phone) {
    var d = (phone || '').replace(/\D/g, '');
    if (!d) return null;                                   // empty — omit from payload
    if (d.length === 10)                 return '+1' + d;  // US 10-digit
    if (d.length === 11 && d[0] === '1') return '+' + d;  // US 11-digit with country code
    return '+' + d;                                        // international — best effort E.164
  }

  function splitName(full) {
    var p = (full || '').trim().split(/\s+/);
    return { first: p[0] || '', last: p.slice(1).join(' ') || '' };
  }

  async function sha256hex(str) {
    var buf = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode((str || '').trim().toLowerCase())
    );
    return Array.from(new Uint8Array(buf))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  function buildProfileAttrs(data) {
    var n     = splitName(data.name);
    var phone = normalizePhone(data.phone);
    var attrs = {
      email:      data.email,
      first_name: n.first,
      last_name:  n.last,
      properties: {
        product_title: data.productTitle,
        product_url:   data.productUrl,
        product_price: data.productPrice,
      },
    };
    if (phone) attrs.phone_number = phone;
    attrs.properties = Object.assign({}, attrs.properties || {}, data.tracking || {});
    return attrs;
  }

  // The Subscriptions API needs an explicit consent block, or it will
  // create/update the profile without actually opting them into the list —
  // that's why profiles were showing up in Profiles search but not in the
  // list membership. SMS consent is deliberately NOT included here since
  // this form has no separate SMS opt-in checkbox; adding it would be
  // legally risky (TCPA) without the user explicitly consenting to texts.
  function buildSubscriptionAttrs(data) {
    var attrs = buildProfileAttrs(data);
    attrs.subscriptions = {
      email: {
        marketing: {
          consent: 'SUBSCRIBED',
        }
      }
    };
    attrs.properties = Object.assign({}, attrs.properties || {}, data.tracking || {});
    return attrs;
  }

  async function subscribeToList(data) {
    var profileAttrs = buildSubscriptionAttrs(data);
    var res = await sendKlaviyo(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: CONFIG.klaviyoListId } }
            }
          }
        }),
      }
    );
    return res;
  }

  async function submitToKlaviyo(data) {
    var profileAttrs = buildProfileAttrs(data);
    var res = await sendKlaviyo(
      'https://a.klaviyo.com/client/events/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: {
                  type:       'metric',
                  attributes: { name: CONFIG.klaviyoEventName + (isTrackingTest() ? ' TEST' : '') }
                }
              },
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              },
              unique_id: data.deliveryId,
              properties: {
                ...(data.tracking || {}),
                gtd_source_form_at: data.submittedAt,
                gtd_browser_time_basis: 'device_clock',
                gtd_tracking_test: isTrackingTest(),
                message:       data.message,
                phone:         data.phone,
                product_title: data.productTitle,
                product_url:   data.productUrl,
                product_price: data.productPrice,
                source_url:    window.location.href,
              }
            }
          }
        }),
      }
    );
    return res;
  }

  async function fireDataLayer(data) {
    var results = await Promise.allSettled([sha256hex(data.email), sha256hex(data.phone)]);
    var payload = {
      event:         CONFIG.gtmEventName,
      email:         data.email,
      phone:         data.phone,
      hashedEmail:   results[0].status === 'fulfilled' ? results[0].value : '',
      hashedPhone:   results[1].status === 'fulfilled' ? results[1].value : '',
      product_title: data.productTitle,
      product_url:   data.productUrl,
      product_price: data.productPrice,
      source_url:    window.location.href,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function initForm(form) {
    var productId    = form.id.slice(FORM_ID_PREFIX.length);
    var container     = document.getElementById('quote-form-container-' + productId);
    var thankYou      = document.getElementById('quote-thank-you-' + productId);
    if (!container || !thankYou) {
      return;
    }


    if (form.dataset.gtdKlaviyoBound === '1') return;
    form.dataset.gtdKlaviyoBound = '1';
    var lastAttempt = null, lastSignature = null, submitting = false;
    var submitBtn = form.querySelector('[type="submit"]');
    var btnText   = submitBtn ? submitBtn.querySelector('.quote-btn-text') : null;

    function showThankYou() {
      container.style.display = 'none';
      thankYou.style.display  = 'flex';
      thankYou.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Shopify can complete a protected native submission independently of the async provider request.
    // Its rendered success marker is the source of truth for that route.
    if (form.querySelector('[data-gtd-native-form]')?.getAttribute('data-gtd-result') === 'accepted' &&
        window.location.hash === '#' + form.id) {
      showThankYou();
      const overlay = thankYou.closest('[data-inquiry-overlay]');
      if (overlay) {overlay.classList.add('is-open'); overlay.setAttribute('aria-hidden', 'false');}
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (submitting) return;
      submitting = true;
      const priorButtonText = btnText?.textContent || 'Send';
      const previousError = form.querySelector('[data-gtd-delivery-error]');
      if (previousError) previousError.remove();
      if (submitBtn) submitBtn.disabled = true;
      if (btnText)   btnText.textContent = 'Sending…';

      var v = function (sel) {
        var el = form.querySelector(sel);
        return el ? el.value : '';
      };
      var data = {
        name:         v('[name="contact[name]"]'),
        email:        v('[name="contact[email]"]'),
        phone:        v('[name="contact[phone]"]'),
        message:      v('[name="contact[body]"]'),
        productTitle: form.getAttribute('data-product-title') || '',
        productUrl:   form.getAttribute('data-product-url') || '',
        productPrice: form.getAttribute('data-product-price') || '',
      };

      const signature = JSON.stringify(data);
      if (lastAttempt && signature === lastSignature) data = lastAttempt;
      else {
        data.tracking = window.GTDNativeFormTracking?.capture(form) || window.GTDInquiryTracking?.capture(form.id, 'product_quote') || {};
        data.deliveryId = data.tracking.gtd_inquiry_id || crypto.randomUUID();
        data.submittedAt = new Date().toISOString();
        lastAttempt = data; lastSignature = signature;
      }
      var normalisedPhone = normalizePhone(data.phone);

      // 0. Klaviyo JS SDK identify — updates phone on the profile immediately,
      //    works for new AND existing subscribers
      if (window._learnq) {
        var n = splitName(data.name);
        var identifyPayload = {
          '$email':      data.email,
          '$first_name': n.first,
          '$last_name':  n.last,
          product_title: data.productTitle,
        };
        if (normalisedPhone) identifyPayload['$phone_number'] = normalisedPhone;
        try {window._learnq.push(['identify', identifyPayload]);} catch (_) {}
      } else {
      }

      try {
        // Preview uses a real TEST event only; it does not subscribe customers or trigger live lead conversions.
        if (!isTrackingTest() && !data.subscriptionAccepted) {
          await subscribeToList(data); data.subscriptionAccepted = true;
        }
        await submitToKlaviyo(data);
        window.GTDKlaviyoFormTracking?.recordAccepted(buildProfileAttrs(data), data.tracking, data);
        if (!isTrackingTest()) {try {await fireDataLayer(data);} catch (_) {}}
        showThankYou();
        lastAttempt = null; lastSignature = null;
      } catch (_) {
        const error = document.createElement('p');
        error.dataset.gtdDeliveryError = '1'; error.setAttribute('role','alert');
        error.textContent = "We couldn't confirm your request was sent. Your information is still here. Please try again.";
        form.appendChild(error);
      } finally {
        submitting = false;
        if (submitBtn) submitBtn.disabled = false;
        if (btnText) btnText.textContent = priorButtonText;
      }
    });
  }

  function initAll() {
    document.querySelectorAll('form[id^="' + FORM_ID_PREFIX + '"]').forEach(initForm);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);

})();
